import React, { useEffect, useMemo, useState } from 'react';
import marketplacesettingsService from '../../services/marketplacesettingsService';
import CredentialsPicker from './CredentialsPicker';
import CredentialChip from './CredentialChip';
import CredentialPickerScreen from './CredentialPickerScreen';
import CredentialOverrides, { normalizeOverrideDefs, overridesComplete } from './CredentialOverrides';

const HOTEL_ID_KEYS = ['HOTELID', 'HOTEL_ID'];

// A credential's per-property values as one object ({ HOTELID: 'X', OTHER: 'y' }) → the two places they are stored:
// the hotel id in its own column (hotelid_override), the rest as credential_overrides. Every screen that saves a
// binding splits them this way; it is said once here.
export const splitOverrides = (values = {}) => {
    const hotelidOverride = HOTEL_ID_KEYS.map(k => values[k]).find(v => v && String(v).trim()) || null;
    const rest = Object.fromEntries(Object.entries(values)
        .filter(([k, v]) => !HOTEL_ID_KEYS.includes(String(k).toUpperCase()) && v != null && String(v).trim() !== ''));
    return { hotelidOverride, credentialOverrides: Object.keys(rest).length ? rest : null };
};

// …and back: the stored pair as the one object the picker edits.
export const joinOverrides = (hotelidOverride, credentialOverrides) => {
    const parsed = typeof credentialOverrides === 'string'
        ? (() => { try { return JSON.parse(credentialOverrides); } catch (e) { return {}; } })()
        : (credentialOverrides || {});
    // Under both spellings, since a marketplace names the setting one way or the other.
    return { ...parsed, ...(hotelidOverride ? { HOTELID: hotelidOverride, HOTEL_ID: hotelidOverride } : {}) };
};
import CredentialTest from './CredentialTest';
import './CredentialsPicker.css';

// THE credential selector. Selected state, empty state, and the full-screen picker, in one component.
//
// This existed as five hand-copied variants — PaymentPorterPage, VaultForm, OhipHotelCredentialStep,
// OperaCloudCompareDetailPage and (briefly) ToolkitBlocksPage — and they had drifted: different button
// labels, different headings, and only some of them gated the "add" action on a hotel being selected.
// That gate is not cosmetic: choosing a credential SHARES it, and a share needs a hotel to share to, so
// a screen without the gate fails with "Failed to share credential" and gives the user nothing to act
// on.
//
// Anything needing a credential renders this. Nothing re-implements the chip / add-button / full-screen
// dance again.
//
// Props
//   marketplaceId          required — which marketplace's credentials to offer
//   hotelId/accountId/partnerId  the hierarchy searched, and where a new credential can be shared
//   context, contextId     optional — sync|pp|vault binding, passed straight through
//   value                  the selected credential ({ credential_id, credential_name, _entityType }) or null
//   onChange(credential)   fired on pick, create-and-share, and removal (null)
//   canEdit                superadmin — show Edit on each row
//   title / subtitle       heading above the control, and the picker screen's subtitle
//   meta                   extra text on the chip (e.g. an overridden hotel id)
//   requiresShareId        sync needs a real share row to bind the connector to
//   disabled               block the add button (e.g. no hotel chosen yet)
//   disabledReason         shown when disabled — say which control to use, never just grey out
export default function CredentialSelector({
    marketplaceId,
    hotelId, accountId, partnerId,
    context, contextId,
    value,
    onChange,
    canEdit = false,
    title = 'Connection Credentials',
    subtitle = 'Select an existing credential from the hotel, chain or partner — or add a new one.',
    meta,
    requiresShareId = false,
    allowAddAt,
    disabled = false,
    disabledReason,
    error,
    // CONTROLLED OPEN, optional. Most screens let this component own the open/closed state. A few drive
    // it from their own lifecycle — OperaCloudCompareDetailPage opens the picker as part of "editing the
    // source side", with Save/Cancel around it — and for those the parent passes `open` and
    // `onOpenChange`. Uncontrolled is the default so the common case stays a one-liner.
    open: openProp,
    onOpenChange,
    // Extra content rendered INSIDE the full-screen picker, below the list — per-hotel overrides and a
    // Save/Cancel pair, which Payment Porter and Vault both need. Without this they could not adopt the
    // shared component and would have stayed hand-rolled, which is the whole problem this solves.
    children,
    // The row highlighted in the picker, when that differs from the credential shown on the chip. Payment
    // Porter stages a PENDING choice that is not saved until overrides are filled in, so the chip shows
    // the saved credential while the list highlights the pending one. Defaults to `value`.
    pickerValue,
    // PER-HOTEL OVERRIDES, rendered INSIDE this widget directly under the chip.
    //
    // A chain- or partner-level credential is shared across properties, so any setting the marketplace
    // flags must_be_overridden (HOTELID on OHIP — V197) cannot come from the credential itself. That
    // makes the override part of the credential CHOICE, not a separate field that happens to sit near
    // it: pick a partner credential and you have not finished until you have said which property it
    // means. Every screen used to render CredentialOverrides itself, below the selector, and each one
    // had to remember to — a partner credential with no property code is the failure that produces.
    //
    // Opt-in: pass `overrides` + `onOverrideChange`. Screens that manage overrides elsewhere (the
    // useCredentialStep hook, which splits HOTELID into its own hotelid_override column) pass neither
    // and are unaffected. `overrideDefs` is optional — CredentialOverrides loads them from the
    // marketplace when not supplied.
    overrides,
    onOverrideChange,
    overrideDefs,
    overridesTitle = 'Overrides for this hotel',
    // Test the SELECTED credential in place. Opt-in, and separate from the test inside the add form:
    // that one checks values as typed, this one checks the credential as stored — plus whatever
    // overrides are in the boxes above it, which is the combination the runtime will actually use.
    // Tested by id, so the browser never receives the secret.
    showTest = false,
    onTestResult,
    // THE OVERRIDES ARE PART OF THE PICK — asked INSIDE the picker screen, under the list, with Cancel and Save.
    //
    // The house pattern (pipelines, Payment Porter, Vault, Opera Cloud compare, the product wizards, Universal API
    // access): choosing a chain- or partner-level credential stages it, the marketplace's must_be_overridden fields
    // (HOTELID on OHIP) appear beneath it, and nothing is handed back until they are filled and Save is pressed. A
    // hotel-level credential, or one whose system needs nothing per property, is handed back on the click as before.
    // Each of those screens used to build this itself — pending state, the override form, the buttons — and they
    // looked and behaved differently.
    //
    // With it on: `overrides` is the saved values as one object ({ HOTELID, … } — joinOverrides builds it from the
    // stored pair), and onChange is called as onChange(credential, overrides) — splitOverrides turns them back into
    // hotelid_override + credential_overrides. The chip shows the saved values, and Change reopens the picker on them.
    overridesInPicker = false,
    // Only the picker screen, no chip or add button — for a caller that draws its own trigger (a pipeline board's
    // target cell) and drives `open` itself.
    screenOnly = false,
    // overridesInPicker: also offer an OPTIONAL hotel id override when the system names no required per-property
    // field — Payment Porter's, where a credential's own Hotel ID may be replaced for one property.
    allowHotelIdOverride = false,
}) {
    const [openState, setOpenState] = useState(false);
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : openState;
    const setOpen = (v) => (isControlled ? onOpenChange && onOpenChange(v) : setOpenState(v));

    // overridesInPicker: the row chosen in the picker and the values typed for it, until Save.
    const [pending, setPending] = useState(null);
    const [draft, setDraft] = useState({});
    const [schema, setSchema] = useState(null);
    useEffect(() => {
        if (!overridesInPicker || !open) return;
        setPending(value && value.credential_id != null ? value : null);
        setDraft(overrides || {});
        // overrides/value are read as they are when the screen opens, not followed while it is open.
        // eslint-disable-next-line
    }, [overridesInPicker, open]);
    useEffect(() => {
        if (!overridesInPicker || overrideDefs || !marketplaceId) return undefined;
        let cancelled = false;
        marketplacesettingsService.getSchema({ MARKETPLACEID: marketplaceId })
            .then(res => { if (!cancelled) setSchema(res?.settings || []); })
            .catch(() => { if (!cancelled) setSchema([]); });
        return () => { cancelled = true; };
    }, [overridesInPicker, overrideDefs, marketplaceId]);
    const rawDefs = overrideDefs || schema || [];
    const defs = useMemo(() => normalizeOverrideDefs(rawDefs), [rawDefs]);
    const levelOf = (c) => (c ? (c._entityType || c.entity_type || 'hotel') : null);
    const needsOverrides = (c) => ['account', 'partner'].includes(levelOf(c)) && defs.length > 0;
    // Staged in the picker either for the required fields, or for the optional hotel id override.
    // A hotel-level credential is already this property's, so it is handed back on the click either way.
    const asksInPicker = (c) => needsOverrides(c)
        || (allowHotelIdOverride && defs.length === 0 && ['account', 'partner'].includes(levelOf(c)));

    const commit = (credential, values) => { onChange(credential, values); setOpen(false); };
    const stage = (credential) => {
        if (!asksInPicker(credential)) { commit(credential, {}); return; }
        setPending(credential);
        // The values already saved belong to the credential they were saved with; another one starts empty.
        setDraft(value && String(value.credential_id) === String(credential.credential_id) ? (overrides || {}) : {});
    };

    // Rendered INSTEAD of the caller's content, not over it — the site pattern is a full screen with a
    // Back link, so the picker has room for three levels of credentials without a scrolling modal.
    if (open) {
        return (
            <CredentialPickerScreen title={title} subtitle={subtitle} onBack={() => setOpen(false)}>
                {error && <p className="cred-picker-error">{error}</p>}
                <CredentialsPicker
                    tabbed
                    canEdit={canEdit}
                    marketplaceId={marketplaceId}
                    context={context}
                    contextId={contextId}
                    hotelId={hotelId}
                    accountId={accountId}
                    partnerId={partnerId}
                    requiresShareId={requiresShareId}
                    {...(allowAddAt ? { allowAddAt } : {})}
                    selection={(() => {
                        const sel = (overridesInPicker && pending) || (pickerValue !== undefined ? pickerValue : value);
                        return sel?.credential_id != null ? { credential_id: sel.credential_id } : null;
                    })()}
                    onSelect={(sel) => (overridesInPicker ? stage(sel) : (onChange(sel), setOpen(false)))}
                    // A newly created credential arrives with shareId rather than share_id; normalising
                    // here means callers never have to know that.
                    onCreateAndShare={(payload) => {
                        const sel = { ...payload, share_id: payload.shareId };
                        if (overridesInPicker) { stage(sel); return; }
                        onChange(sel);
                        setOpen(false);
                    }}
                />
                {overridesInPicker && pending && asksInPicker(pending) && (
                    <div className="cred-picker-confirm">
                        {!needsOverrides(pending) && (
                            <div className="cred-overrides">
                                <div className="cred-overrides-field">
                                    <label className="cred-overrides-label" htmlFor="cred-picker-hotelid">
                                        Hotel ID override
                                        <span className="cred-overrides-hint"> — leave blank to use the Hotel ID from the selected credential</span>
                                    </label>
                                    <input
                                        id="cred-picker-hotelid"
                                        className="cred-overrides-input"
                                        value={draft.HOTELID || ''}
                                        onChange={(e) => setDraft(d => ({ ...d, HOTELID: e.target.value, HOTEL_ID: e.target.value }))}
                                        placeholder="e.g. LONHILTON"
                                    />
                                </div>
                            </div>
                        )}
                        <CredentialOverrides
                            marketplaceId={marketplaceId}
                            level={levelOf(pending)}
                            defs={rawDefs}
                            values={draft}
                            // The hotel id under both spellings, so whichever the marketplace uses is the one saved.
                            onChange={(name, val) => setDraft(d => (HOTEL_ID_KEYS.includes(String(name).toUpperCase())
                                ? { ...d, HOTELID: val, HOTEL_ID: val } : { ...d, [name]: val }))}
                            title={`${overridesTitle} — with ${pending.credential_name || 'this credential'}`}
                        />
                        <div className="cred-picker-confirm-actions">
                            <button type="button" className="cred-picker-cancel" onClick={() => setOpen(false)}>Cancel</button>
                            <button
                                type="button"
                                className="cred-picker-save"
                                disabled={!overridesComplete(defs, draft)}
                                title={overridesComplete(defs, draft) ? undefined : 'Fill in every field for this property first'}
                                onClick={() => commit(pending, draft)}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                )}
                {children}
            </CredentialPickerScreen>
        );
    }

    if (screenOnly) return null;

    // The saved per-property values on the chip, when the picker owns them.
    // Only the fields asked per property (a caller may hand over more — Vault passes every source setting), plus the
    // optional hotel id override where that is offered.
    const shownKeys = new Set([...defs.map(d => d.name), ...(allowHotelIdOverride ? HOTEL_ID_KEYS : [])]);
    const savedMeta = overridesInPicker && overrides
        ? Object.entries(overrides)
            .filter(([k, v]) => v && shownKeys.has(k) && !(k === 'HOTEL_ID' && overrides.HOTELID))
            .map(([k, v]) => `${HOTEL_ID_KEYS.includes(k) ? 'Hotel ID' : ((defs.find(d => d.name === k) || {}).label || k)}: ${v}`)
            .join(' · ') || null
        : null;

    return (
        <div className="cred-selector">
            {title && <div className="cred-selector-title">{title}</div>}
            {error && <p className="cred-picker-error">{error}</p>}
            {value ? (
                // One card when there are overrides: the chip is its header row and the overrides sit
                // inside it, so the credential and the per-hotel values it needs read as one control.
                // Two separate boxes said "here is a credential, and here is an unrelated form".
                <div className={onOverrideChange && !overridesInPicker ? 'cred-selected-group' : undefined}>
                    <CredentialChip
                        name={value.credential_name}
                        level={value._entityType || value.entity_type}
                        meta={meta || savedMeta || undefined}
                        onChange={() => setOpen(true)}
                        onRemove={() => onChange(null)}
                    />
                    {/* Renders nothing for a hotel-level credential (CredentialOverrides' activeLevels
                        are account/partner) — one is already specific to this property, so there is
                        nothing to override. */}
                    {onOverrideChange && !overridesInPicker && (
                        <div className="cred-selector-overrides">
                            <CredentialOverrides
                                marketplaceId={marketplaceId}
                                level={value._entityType || value.entity_type}
                                values={overrides || {}}
                                onChange={onOverrideChange}
                                title={overridesTitle}
                                {...(overrideDefs ? { defs: overrideDefs } : {})}
                            />
                        </div>
                    )}
                    {showTest && (
                        <div className="cred-selector-test">
                            <CredentialTest
                                marketplaceId={marketplaceId}
                                credentialId={value.credential_id}
                                overrides={overrides
                                    ? Object.entries(overrides).map(([name, v]) => ({ name, value: v }))
                                    : undefined}
                                onResult={onTestResult}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <button type="button" className="cred-add-btn" onClick={() => setOpen(true)} disabled={disabled}>
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        Add Credential
                    </button>
                    {disabled && disabledReason && <p className="cred-selector-hint">{disabledReason}</p>}
                </>
            )}
        </div>
    );
}
